import { useEffect, useMemo, useState } from 'react';
import { showToast } from '@/components/ui/Toast';
import { t } from '@/lib/i18n';
import {
  useRorosActorPersona,
  useRorosActorPersonaUpsert,
} from '@/hooks/roros/actors';
import type { RorosActorDTO } from '@/types/roros/actors';
import { PERSONA_DEFAULTS, type PersonaLocalState } from '../UnifiedActorEditor.model';

interface UseActorPersonaParams {
  actor?: RorosActorDTO | null;
  isOpen: boolean;
  showPersona: boolean;
  isKnowledgePersona: boolean;
}

/**
 * Owns the "persona" concern of UnifiedActorEditor: the persona query/upsert,
 * local persona-form state, init-from-query + reset-on-close effects, the
 * domain-stance map/handler, and the persona-save handler. The local
 * `personaState` (+ setters) is returned so the form inputs and the main save
 * orchestrator keep consuming it unchanged. Behaviour-identical extraction
 * (M10f decomposition).
 */
export function useActorPersona({ actor, isOpen, showPersona, isKnowledgePersona }: UseActorPersonaParams) {
  const personaQuery = useRorosActorPersona(showPersona ? (actor?.id ?? '') : '');
  const upsertPersonaMutation = useRorosActorPersonaUpsert();
  const [personaState, setPersonaState] = useState<PersonaLocalState>(PERSONA_DEFAULTS);
  const [personaInitialized, setPersonaInitialized] = useState(false);
  const [personaDirty, setPersonaDirty] = useState(false);

  useEffect(() => {
    if (personaQuery.data && !personaInitialized && isOpen) {
      setPersonaState({
        voiceDescription: personaQuery.data.voiceDescription ?? '',
        styleDescription: personaQuery.data.styleDescription ?? '',
        valuesPriorities: personaQuery.data.valuesPriorities ?? '',
        decisionStyle: personaQuery.data.decisionStyle ?? '',
        primaryLanguage: personaQuery.data.primaryLanguage ?? 'en',
        languageRules: personaQuery.data.languageRules ?? '',
        domainStances: Array.isArray(personaQuery.data.domainStances) ? personaQuery.data.domainStances : [],
        provider: personaQuery.data?.provider ?? null,
        modelTier: (personaQuery.data as any)?.modelTier ?? null,
        instructions: (personaQuery.data as any)?.instructions ?? null,
      });
      setPersonaInitialized(true);
    }
  }, [personaQuery.data, personaInitialized, isOpen]);

  // Reset persona on close
  useEffect(() => {
    if (!isOpen) {
      setPersonaInitialized(false);
      setPersonaDirty(false);
      setPersonaState(PERSONA_DEFAULTS);
    }
  }, [isOpen]);

  const stanceMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const s of personaState.domainStances) {
      map.set(s.domainId, s.stance);
    }
    return map;
  }, [personaState.domainStances]);

  const handleDomainStanceChange = (domainId: number, stance: string) => {
    setPersonaState((prev) => {
      const updated = prev.domainStances.filter((s) => s.domainId !== domainId);
      updated.push({ domainId, stance });
      return { ...prev, domainStances: updated };
    });
    setPersonaDirty(true);
  };

  const handlePersonaSave = async () => {
    if (!actor?.id) return;
    try {
      await upsertPersonaMutation.mutateAsync({
        actorId: actor.id,
        payload: {
          voiceDescription: personaState.voiceDescription === '' ? null : (personaState.voiceDescription || undefined),
          styleDescription: personaState.styleDescription === '' ? null : (personaState.styleDescription || undefined),
          valuesPriorities: personaState.valuesPriorities === '' ? null : (personaState.valuesPriorities || undefined),
          decisionStyle: personaState.decisionStyle === '' ? null : (personaState.decisionStyle || undefined),
          primaryLanguage: personaState.primaryLanguage,
          languageRules: personaState.languageRules === '' ? null : (personaState.languageRules || undefined),
          domainStances: personaState.domainStances.filter((s) => s.stance.trim().length > 0),
          provider: isKnowledgePersona ? personaState.provider : personaState.provider,
          modelTier: isKnowledgePersona ? (personaState.modelTier ?? null) : undefined,
          instructions: isKnowledgePersona ? (personaState.instructions ?? null) : undefined,
        },
      });
      setPersonaDirty(false);
      showToast.success(t('Persona saved'));
    } catch (err) {
      showToast.error(err instanceof Error ? err.message : t('Failed to save persona'));
    }
  };

  return {
    personaQuery,
    upsertPersonaMutation,
    personaState,
    setPersonaState,
    personaInitialized,
    personaDirty,
    setPersonaDirty,
    stanceMap,
    handleDomainStanceChange,
    handlePersonaSave,
  };
}
